import React, { useState } from "react";
import {
  Card,
  Text,
  Group,
  Box,
  Title,
  Button,
  Badge,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import AddOperatingLevelTypeModal from "./AddOperatingLevelTypeModal";
import EditOperatingLevelTypeModal from "./EditOperatingLevelTypeModal";

const OperatingLevelTypeNodeBox = ({ node, onEdit }) => {
  const isDefault = node.base_operation_type;
  const isTop = node.top_operation_type;
  const isEmphasized = isDefault || isTop;

  return (
    <Box
      style={{
        padding: ".5rem .75rem",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        whiteSpace: "nowrap",
      }}
    >
      <Group gap="xs" align="center" wrap="nowrap" justify="center">
        <Text style={{ fontWeight: isEmphasized ? 700 : 500 }}>
          {node.name}
        </Text>
        {isDefault && (
          <Badge size="xs" color="grape" variant="light">
            Base
          </Badge>
        )}
        {isTop && (
          <Badge size="xs" color="teal" variant="light">
            Top
          </Badge>
        )}
        {onEdit && (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onEdit(node)}
            aria-label={`Edit ${node.name}`}
          >
            <IconEdit size={16} />
          </ActionIcon>
        )}
      </Group>
      {isTop && (
        <Text
          size="xs"
          c="dimmed"
          mt={4}
          ta="center"
          style={{ whiteSpace: "normal", maxWidth: 220 }}
        >
          * The Top operations type is for your entire company and cannot be
          deleted.
        </Text>
      )}
      {isDefault && (
        <Text
          size="xs"
          c="dimmed"
          mt={4}
          ta="center"
          style={{ whiteSpace: "normal", maxWidth: 220 }}
        >
          * The Base operations type is for the offices or local operations
          locations that teams and sales are assigned to and it cannot be
          deleted.
        </Text>
      )}
    </Box>
  );
};

const OperatingLevelTypeTreeNode = ({ node, onEdit }) => {
  const hasChildren = node.sub_types && node.sub_types.length > 0;
  const isSynthetic = node.synthetic_root;

  return (
    <Box
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 .5rem",
      }}
    >
      {!isSynthetic && (
        <OperatingLevelTypeNodeBox node={node} onEdit={onEdit} />
      )}
      {hasChildren && (
        <>
          {!isSynthetic && (
            <Box style={{ width: 2, height: 20, backgroundColor: "#ccc" }} />
          )}
          <Box style={{ display: "flex", alignItems: "flex-start" }}>
            {node.sub_types.map((child, i) => {
              const isFirst = i === 0;
              const isLast = i === node.sub_types.length - 1;
              const showHorizontal = node.sub_types.length > 1;

              return (
                <Box
                  key={child.id ?? child.name ?? i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    paddingTop: isSynthetic ? 0 : 20,
                  }}
                >
                  {!isSynthetic && showHorizontal && (
                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        left: isFirst ? "50%" : 0,
                        right: isLast ? "50%" : 0,
                        height: 2,
                        backgroundColor: "#ccc",
                      }}
                    />
                  )}
                  {!isSynthetic && (
                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 2,
                        height: 20,
                        backgroundColor: "#ccc",
                      }}
                    />
                  )}
                  <OperatingLevelTypeTreeNode node={child} onEdit={onEdit} />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

const OperatingLevelTypesCard = ({
  operatingLevelTypes = [],
  typesHierarchy = null,
  onRefresh,
  embedded = false,
}) => {
  const { getVerifiedJWT } = useAuth();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOperatingLevelType, setSelectedOperatingLevelType] =
    useState(null);

  const fetchOperatingLevelTypes = async () => {
    if (onRefresh) await onRefresh();
  };

  const handleAddOperatingLevelType = async newType => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/operations/type"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        name: newType.name,
        operation_type_parent_id: newType.operation_type_parent_id,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create operations type");
    }

    await fetchOperatingLevelTypes();
  };

  const handleUpdateOperatingLevelType = async updatedType => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(`/v1/operations/type/${updatedType.id}`),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ name: updatedType.name }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to update operations type");
    }

    await fetchOperatingLevelTypes();
  };

  const handleDeleteOperatingLevelType = async operatingLevelTypeId => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(`/v1/operations/type/${operatingLevelTypeId}`),
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to delete operations type");
    }

    await fetchOperatingLevelTypes();
  };

  const handleEditClick = type => {
    setSelectedOperatingLevelType(type);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedOperatingLevelType(null);
  };

  const content = (
    <>
      <Group justify={embedded ? "flex-end" : "space-between"} mb="md">
        {!embedded && <Title order={3}>Operations Structure</Title>}
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setAddModalOpen(true)}
          variant="filled"
          size="sm"
        >
          Add Operations Type
        </Button>
      </Group>

      {!operatingLevelTypes || operatingLevelTypes.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No operation types configured yet
        </Text>
      ) : (
        typesHierarchy && (
          <Box
            style={{
              overflowX: "auto",
              paddingBottom: ".5rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <OperatingLevelTypeTreeNode
              node={typesHierarchy}
              onEdit={handleEditClick}
            />
          </Box>
        )
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {content}
        </Card>
      )}

      <AddOperatingLevelTypeModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddOperatingLevelType}
        operatingLevelTypes={operatingLevelTypes}
      />

      <EditOperatingLevelTypeModal
        opened={editModalOpen}
        onClose={handleEditClose}
        onSave={handleUpdateOperatingLevelType}
        onDelete={handleDeleteOperatingLevelType}
        operatingLevelType={selectedOperatingLevelType}
      />
    </>
  );
};

export default OperatingLevelTypesCard;
